import { type AnyAction } from 'redux';
import {
	call,
	put,
	takeLatest,
	type CallEffect,
	type ForkEffect,
	type PutEffect,
	type SelectEffect
} from 'redux-saga/effects';

import { ProductsActionType } from './enums';

import { uploadDocumentArr, transformUploadData, handleEmptyNullValues, sanitizeObjectStrings } from '@utilities';
import { uploadFile } from '@utilities/uploader';
import { coerceDefaultModelsRows } from '@utilities/default-subcategory-models';
import {
	activateDocument,
	addCategorySubCategory,
	addProductItem,
	addProductGroup,
	addWarranty,
	addXorGroup,
	bulkEditProducts,
	bulkEditWarranty,
	deleteBookingDocuments,
	deleteCategory,
	deleteProduct,
	deleteProductGroup,
	deleteSubCategory,
	deleteWarranty,
	deleteXorGroup,
	fetchAllCategories,
	fetchAllCategoriesDropdown,
	fetchAllProductItemsDropdown,
	fetchAllProductItemsDropdownV2,
	fetchAllProducts,
	fetchAllProductWarranty,
	fetchAllSubCategoriesDropdown,
	fetchAreaListDropdown,
	fetchProductAreaListDropdown,
	fetchProductDetails,
	fetchProductItemHistory,
	fetchProductTaxonomy,
	fetchWarrantyDetailsByCodes,
	fetchInternalCategories,
	updateCategorySubCategory,
	updateCoverImage,
	updateProductGroup,
	updateProductDocuments,
	updateProductItem,
	uploadDocument,
	updateWarranty,
	updateXorGroup,
	deleteModelAsset,
	fetchDefaultModels,
	updateSubCategoryDefaultModel
} from './api';
import { fetchQuotationToolList } from '@store/branches/leads/api';

type ProductsSagaEffect = Generator<CallEffect<any> | PutEffect<any> | SelectEffect> | any;

type ProductsSagaFormEffect = Generator<ForkEffect<void>>;

const getErrorMessage = (error: unknown) =>
	error && typeof error === 'object' && 'message' in error ? String(error.message) : 'Something went wrong';

export function* fetchAllCategoriesEffect(action: AnyAction): ProductsSagaEffect {
	const { offset, pageSize, errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchAllCategories, {
			pageSize,
			offset,
			filters: {
				active: true
			}
		});

		yield put({ type: ProductsActionType.STORE_ALL_INSPIRATIONS_CATEGORY, payload: data });
	} catch (e: any) {
		yield put({
			type: ProductsActionType.ERROR_ALL_INSPIRATIONS_CATEGORY,
			payload: {
				e
			}
		});
		errorCB(e?.message);
	}
}
export function* addCategorySubcategoryEffect(action: AnyAction): ProductsSagaEffect {
	const { values, errorCB, successCB, finalCB } = action.payload;

	try {
		const { data } = yield call(addCategorySubCategory, { ...values.transformedPayload });
		yield put({ type: ProductsActionType.STORE_CATEGORY_SUBCATEGORY, payload: data });
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

export function* updateCategorySubcategoryEffect(action: AnyAction): ProductsSagaEffect {
	const { values, errorCB, successCB, finalCB, initialValues } = action.payload;

	try {
		const initialSubCategoryNames = initialValues.subCategory.map(sub => sub);

		// Filter out new subcategories from current values
		const newSubCategories = values.subCategory.filter(
			sub => !initialSubCategoryNames.includes(sub.subCategoryName)
		);

		if (newSubCategories.length > 0) {
			try {
				const { data } = yield call(updateCategorySubCategory, {
					subCategory: newSubCategories,
					categoryId: values.categoryId
				});
				yield put({ type: ProductsActionType.STORE_CATEGORY_SUBCATEGORY, payload: data });
				successCB();
			} catch (error: unknown) {
				errorCB(error?.message);
			}
		}
	} finally {
		finalCB();
	}
}

export function* deleteCategoryEffect(action: AnyAction): ProductsSagaEffect {
	const { categoryId, errorCB, finalCB } = action.payload;
	try {
		yield call(deleteCategory, categoryId);
	} catch (error: unknown) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

export function* deleteSubCategoryEffect(action: AnyAction) {
	const { subCategoryId, errorCB } = action.payload;
	try {
		yield call(deleteSubCategory, { ids: [subCategoryId] });
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

const normalizeTaxonomyList = (data: Record<string, any>, camelKey: string, snakeKey: string) => {
	const value = data?.[camelKey] ?? data?.[snakeKey] ?? data?.[camelKey.replace(/s$/, '')] ?? data;
	return Array.isArray(value) ? value : [];
};

export function* fetchProductTaxonomyEffect(action: AnyAction): ProductsSagaEffect {
	const { errorCB } = action.payload || {};
	try {
		const { data }: any = yield call(fetchProductTaxonomy);
		yield put({
			type: ProductsActionType.STORE_PRODUCT_TAXONOMY,
			payload: {
				xorGroups: normalizeTaxonomyList(data, 'xorGroups', 'xor_groups'),
				productGroups: normalizeTaxonomyList(data, 'productGroups', 'product_groups')
			}
		});
	} catch (error: unknown) {
		yield put({
			type: ProductsActionType.ERROR_PRODUCT_TAXONOMY,
			payload: {
				error
			}
		});
		errorCB?.(getErrorMessage(error));
	}
}

export function* saveXorGroupEffect(action: AnyAction): ProductsSagaEffect {
	const { values, errorCB, successCB, finalCB } = action.payload;
	try {
		if (values.id) {
			yield call(updateXorGroup, values.id, values);
		} else {
			yield call(addXorGroup, values);
		}
		successCB?.();
	} catch (error: unknown) {
		errorCB?.(getErrorMessage(error));
	} finally {
		finalCB?.();
	}
}

export function* saveProductGroupEffect(action: AnyAction): ProductsSagaEffect {
	const { values, errorCB, successCB, finalCB } = action.payload;
	try {
		if (values.id) {
			yield call(updateProductGroup, values.id, values);
		} else {
			yield call(addProductGroup, values);
		}
		successCB?.();
	} catch (error: unknown) {
		errorCB?.(getErrorMessage(error));
	} finally {
		finalCB?.();
	}
}

export function* deleteXorGroupEffect(action: AnyAction): ProductsSagaEffect {
	const { id, errorCB, finalCB } = action.payload;
	try {
		yield call(deleteXorGroup, id);
	} catch (error: unknown) {
		errorCB?.(getErrorMessage(error));
	} finally {
		finalCB?.();
	}
}

export function* deleteProductGroupEffect(action: AnyAction): ProductsSagaEffect {
	const { id, errorCB, finalCB } = action.payload;
	try {
		yield call(deleteProductGroup, id);
	} catch (error: unknown) {
		errorCB?.(getErrorMessage(error));
	} finally {
		finalCB?.();
	}
}

export function* fetchAllProductsEffect(action: AnyAction): ProductsSagaEffect {
	const { offset, pageSize, errorCB, filters } = action.payload;
	const updatedFilter = {
		...filters,
		area: filters?.area?.key
	};
	delete filters.area;

	try {
		const { data }: any = yield call(fetchAllProducts, {
			pageSize,
			offset,
			filters: updatedFilter
		});

		yield put({ type: ProductsActionType.STORE_ALL_INSPIRATIONS_PRODUCTS, payload: data });
	} catch (e: any) {
		yield put({
			type: ProductsActionType.ERROR_ALL_INSPIRATIONS_PRODUCTS,
			payload: {
				e
			}
		});
		errorCB(e?.message);
	}
}

export function* fetchAllProductCategoriesEffect(action: AnyAction): ProductsSagaEffect {
	const { errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchAllCategoriesDropdown);

		yield put({ type: ProductsActionType.STORE_PRODUCT_CATEGORIES, payload: data.category });
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* fetchAllProductSubCategoriesEffect(action: AnyAction): ProductsSagaEffect {
	const { categoryId, errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchAllSubCategoriesDropdown, categoryId);

		yield put({ type: ProductsActionType.STORE_PRODUCT_SUB_CATEGORIES, payload: data.subCategory });
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* deleteProductEffect(action: AnyAction): ProductsSagaEffect {
	const { itemId, itemIds, errorCB, successCB, finalCB } = action.payload;
	try {
		const ids = Array.isArray(itemIds) && itemIds.length > 0 ? itemIds : [itemId];
		yield call(deleteProduct, { ids });
		successCB?.();
	} catch (error: unknown) {
		errorCB?.((error as Error)?.message);
	} finally {
		finalCB?.();
	}
}
export function* fetchProductDetailsEffect(action: AnyAction): ProductsSagaEffect {
	const { itemId, errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchProductDetails, itemId);
		yield put({ type: ProductsActionType.STORE_PRODUCT_DETAILS, payload: data });
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* fetchProductItemHistoryEffect(action: AnyAction): ProductsSagaEffect {
	const { itemId, limit = 50, errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchProductItemHistory, itemId, limit);
		yield put({ type: ProductsActionType.STORE_PRODUCT_ITEM_HISTORY, payload: data });
	} catch (error: any) {
		yield put({
			type: ProductsActionType.ERROR_PRODUCT_ITEM_HISTORY,
			payload: {
				error
			}
		});
		errorCB?.(error?.data?.message || error?.message || 'Failed to fetch product item history');
	}
}
export function* fetchAllProductWarrantyEffect(action: AnyAction): ProductsSagaEffect {
	const { offset, pageSize, errorCB, filters } = action.payload;
	try {
		const { data }: any = yield call(fetchAllProductWarranty, {
			pageSize,
			offset,
			filters
		});
		yield put({ type: ProductsActionType.STORE_ALL_PRODUCTS_WARRANTY, payload: data });
	} catch (e: any) {
		yield put({
			type: ProductsActionType.ERROR_ALL_PRODUCTS_WARRANTY,
			payload: {
				e
			}
		});
		errorCB(e?.message);
	}
}

export function* fetchAllProductItemsDropdownEffect(action: AnyAction): ProductsSagaEffect {
	const { subCategoryId, errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchAllProductItemsDropdown, { subCategoryId });

		yield put({ type: ProductsActionType.STORE_PRODUCT_ITEMS_DROPDOWN, payload: data.item });
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* fetchAllProductItemsDropdownV2Effect(action: AnyAction): ProductsSagaEffect {
	const { area, errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchAllProductItemsDropdownV2, area);

		yield put({ type: ProductsActionType.STORE_PRODUCT_ITEMS_DROPDOWN, payload: data.item });
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* addWarrantyEffect(action: AnyAction): ProductsSagaEffect {
	const { transformedPayload, errorCB, successCB, finalCB } = action.payload;

	try {
		yield call(addWarranty, transformedPayload);
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

export function* updateWarrantyEffect(action: AnyAction): ProductsSagaEffect {
	const { warrantyId, transformedPayload, errorCB, successCB, finalCB } = action.payload;

	try {
		yield call(updateWarranty, warrantyId, transformedPayload);
		successCB?.();
	} catch (error: unknown) {
		errorCB?.(error?.message);
	} finally {
		finalCB?.();
	}
}

export function* addProductItemEffect(action: AnyAction): ProductsSagaEffect {
	const { formData, errorCB, successCB } = action.payload;
	const currentFileType = 'MEDIA';

	const toAreaEnum = (areaValue: any): string => {
		if (!areaValue) return '';
		if (typeof areaValue === 'object') {
			if (Array.isArray(areaValue)) return toAreaEnum(areaValue[0]);
			return areaValue.areaEnum || areaValue.key || areaValue.value || '';
		}
		if (typeof areaValue === 'string') {
			const trimmedArea = areaValue.trim();
			if (!trimmedArea) return '';
			if (trimmedArea.startsWith('{')) {
				try {
					return toAreaEnum(JSON.parse(trimmedArea));
				} catch {
					return trimmedArea;
				}
			}
			if (/^[A-Z0-9_]+$/.test(trimmedArea)) {
				return trimmedArea.toUpperCase();
			}
			return trimmedArea
				.replace(/[^a-zA-Z0-9\s]/g, '')
				.replace(/\s+/g, '_')
				.toUpperCase();
		}
		return '';
	};

	const toAreaName = (areaValue: any, existingAreaName: any): string => {
		if (typeof existingAreaName === 'string' && existingAreaName.trim()) {
			return existingAreaName.trim();
		}
		if (areaValue && typeof areaValue === 'object') {
			if (Array.isArray(areaValue)) return toAreaName(areaValue[0], '');
			return areaValue.areaName || areaValue.label || '';
		}
		return '';
	};

	const transformedPayload = transformUploadData(formData.document, currentFileType);
	const newFormData = {
		...formData,
		...transformedPayload
	};
	delete newFormData.transformedData;
	const areaEnum = toAreaEnum(newFormData.area) || toAreaEnum(newFormData.areaName);
	const transformedObject = {
		...newFormData,
		// Ensure price calculated in the form is explicitly present in the payload
		price: newFormData.price,
		areaList: areaEnum ? [areaEnum] : [],
		areaName: toAreaName(newFormData.area, newFormData.areaName),
		document: {
			referenceType: newFormData.document[0].referenceType,
			category: newFormData.document[0].category,
			subCategory: newFormData.document[0].subCategory,
			attachments: newFormData.document[0].files
				.filter(file => file.fileName !== newFormData.coverImage.file)
				.map(file => ({
					file: file.fileName
				}))
		}
	};
	delete transformedObject.area;

	// When injecting into a quotation, always send grandTotal = prevQuoteTotal + new line
	const quotationId = transformedObject.quotationId || formData?.quotationId;
	if (quotationId) {
		const quantity = Number(transformedObject.quantity) || 1;
		const unitOrLinePrice = Number(transformedObject.price) || 0;
		const productLineTotal =
			String(transformedObject.unitOfMeasure || '').toUpperCase() === 'SQFT'
				? unitOrLinePrice
				: unitOrLinePrice * quantity;

		try {
			const { data: quotationData }: any = yield call(fetchQuotationToolList, quotationId);
			const previousGrandTotal = Number(quotationData?.grandTotal) || 0;
			transformedObject.grandTotal = Math.round(previousGrandTotal + productLineTotal);
		} catch (fetchError) {
			const fallbackPrev = Number(formData?.grandTotal) || 0;
			// form grandTotal may already be prev+line; if so keep it, else add line onto store/form value
			const combined =
				fallbackPrev > productLineTotal ? fallbackPrev : fallbackPrev + productLineTotal;
			transformedObject.grandTotal = Math.round(combined);
		}
	}

	// Sanitize all string values to ensure ISO-8859-1 compatibility
	const sanitizedObject = sanitizeObjectStrings(handleEmptyNullValues(transformedObject));

	try {
		const { data } = yield call(addProductItem, sanitizedObject);

		// Only attempt file upload when the form actually has files to upload.
		// When imageUrl is pre-filled (e.g. "Mark Global" flow), files array is empty
		// and the API returns no document response — calling uploadDocumentArr with
		// undefined causes "Cannot read properties of undefined (reading 'entries')".
		const hasFiles = Array.isArray(formData?.document?.files) && formData.document.files.length > 0;
		if (hasFiles) {
			const { document } = data;
			const { arrPromises, uploadedDocuments } = uploadDocumentArr({ documents: [formData?.document] }, [document]);
			yield call(async () => await Promise.all(arrPromises));

			if (uploadedDocuments.length > 0) {
				yield call(activateDocument, { documentIds: uploadedDocuments });
			}
		}
		successCB(data);
	} catch (error: unknown) {
		// Explicitly log the error and payload for debugging Add Product issues
		// eslint-disable-next-line no-console
		console.error('ADD_PRODUCT_ITEM failed:', {
			error,
			errorMessage: error?.message,
			errorStack: error?.stack,
			requestBody: sanitizedObject,
			originalRequestBody: handleEmptyNullValues(transformedObject)
		});
		errorCB(error?.message || 'Failed to add product item');
	}
}

export function* fetchProductAreaListEffect(action: AnyAction): ProductsSagaEffect {
	const { errorCB } = action.payload;

	try {
		const { data }: any = yield call(fetchProductAreaListDropdown);
		yield put({ type: ProductsActionType.STORE_PRODUCT_AREA_LIST_DROPDOWN, payload: data });
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* fetchAreaListDropdownEffect(action: AnyAction): ProductsSagaEffect {
	const { errorCB, quotationID } = action.payload;

	try {
		const { data }: any = yield call(fetchAreaListDropdown, quotationID);

		yield put({ type: ProductsActionType.STORE_QUOTATION_AREA_LIST_DROPDOWN, payload: data });
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* deleteWarrantyEffect(action: AnyAction): ProductsSagaEffect {
	const { warrantyId, errorCB, successCB, finalCB } = action.payload;
	try {
		yield call(deleteWarranty, { ids: [warrantyId] });
		successCB?.();
	} catch (error: unknown) {
		errorCB?.(error?.message);
	} finally {
		finalCB?.();
	}
}

export function* bulkEditWarrantyEffect(action: AnyAction): ProductsSagaEffect {
	const { bulk, successCB, errorCB, finalCB } = action.payload;
	try {
		yield call(bulkEditWarranty, { bulk });
		successCB?.();
	} catch (error: unknown) {
		errorCB?.(error?.message);
	} finally {
		finalCB?.();
	}
}

export function* updateProductItemEffect(action: AnyAction): ProductsSagaEffect {
	const { values, itemId, errorCB, successCB, finalCB } = action.payload;
	const transformedRequestObject = {
		...values,
		unitOfMeasure: values.unitOfMeasure?.key,
		areaList: values.areaList?.map((area: Record<string, any>) => area.key)
	};
	try {
		yield call(updateProductItem, itemId, transformedRequestObject);
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

export function* bulkEditProductsEffect(action: AnyAction): ProductsSagaEffect {
	const { items, successCB, errorCB, finalCB } = action.payload;
	try {
		yield call(bulkEditProducts, { items });
		successCB?.();
	} catch (error: unknown) {
		errorCB?.(error?.message);
	} finally {
		finalCB?.();
	}
}

export function* updateCoverImageEffect(action: AnyAction): ProductsSagaEffect {
	const { documentUrlId, errorCB, finalCB } = action.payload;
	try {
		yield call(updateCoverImage, documentUrlId);
	} catch (error: unknown) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}
export function* updateProductImagesEffect(action: AnyAction): ProductsSagaEffect {
	const { formData, successCB, errorCB, finalCB } = action.payload;
	const currentFileType = 'MEDIA';

	const revisedTransformedObj = transformUploadData(formData, currentFileType);

	const updateProductImageDocRequest = {
		referenceId: revisedTransformedObj.document[0].referenceId,
		referenceType: revisedTransformedObj.document[0].referenceType,
		documents: revisedTransformedObj.document.map((doc: Record<string, any>) => ({
			category: doc.category,
			subCategory: doc.subCategory,
			documentId: doc.documentId,
			attachments: revisedTransformedObj.document[0].files.map((file: Record<string, any>) => ({
				file: file.fileName
			}))
		}))
	};

	const flattenFilesStructure = data => {
		return {
			...data,
			files: data.files?.files || data.files // Flattening if files exist inside another files key
		};
	};

	const uploadProductImageDocRequest = flattenFilesStructure(formData);

	try {
		const { data }: any = yield call(updateProductDocuments, updateProductImageDocRequest);
		const { documentsResponseList } = data;

		const { arrPromises, uploadedDocuments } = uploadDocumentArr(
			{ documents: [uploadProductImageDocRequest] },
			documentsResponseList
		);
		// yield call(async () => await Promise.all(arrPromises));
		yield call(async () => await Promise.all(arrPromises));
		if (uploadedDocuments.length > 0) {
			yield call(activateDocument, { documentIds: uploadedDocuments });
		}

		successCB(documentsResponseList);
	} catch (error: unknown) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

/**
 * Upload a single product image for bulk edit. Same flow as inspiration upload:
 * 1) Hit document API (POST /document/upload) to get upload URLs
 * 2) Upload file to S3 using the response documentUrl (pre-signed URL)
 * 3) Call activate document
 * Returns document_id via successCB(documentId) for the bulk edit payload.
 */
export function* uploadProductImageForBulkEditEffect(action: AnyAction): ProductsSagaEffect {
	const { itemId, file, successCB, errorCB } = action.payload;

	const uploadRequest = {
		reference_id: itemId,
		reference_type: 'PRODUCT',
		documents: [
			{
				category: 'PRODUCT_IMAGES',
				sub_category: 'PRODUCT_ITEM',
				attachments: [{ file: file.name, cover_image: true }],
				document_id: 0
			}
		]
	};

	try {
		const { data }: any = yield call(uploadDocument, uploadRequest);
		const responses = data?.documents_upload_responses ?? data?.documentsUploadResponses;
		if (!responses?.length) {
			errorCB?.('Upload did not return document');
			return;
		}

		const docResponse = responses[0];
		const documentId = docResponse?.document_id ?? docResponse?.documentId;
		const attachments = docResponse?.attachments;
		if (documentId == null || !attachments?.length) {
			errorCB?.('Invalid upload response');
			return;
		}

		const firstAttachment = attachments[0];
		const fileName = firstAttachment?.file_name ?? firstAttachment?.fileName ?? file.name;
		const uploadUrl = firstAttachment?.document_url ?? firstAttachment?.documentUrl;
		if (!uploadUrl) {
			errorCB?.('Upload response missing document_url');
			return;
		}
		yield call(uploadFile, uploadUrl, file, fileName);

		yield call(activateDocument, { documentIds: [documentId] });

		successCB?.(documentId);
	} catch (error: unknown) {
		errorCB?.(error?.message);
	}
}

export function* deleteProductImageEffect(action: AnyAction): ProductsSagaEffect {
	const { values, successCB, errorCB, finalCB } = action.payload;
	try {
		yield call(deleteBookingDocuments, values);
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

export function* fetchAllCategoriesSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.FETCH_ALL_INSPIRATIONS_CATEGORY, fetchAllCategoriesEffect);
}
export function* addCategorySubcategorySaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.ADD_CATEGORY_SUBCATEGORY, addCategorySubcategoryEffect);
}

export function* updateCategorySubcategorySaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.UPDATE_CATEGORY_SUBCATEGORY, updateCategorySubcategoryEffect);
}
export function* deleteCategorySaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.DELETE_CATEGORY, deleteCategoryEffect);
}
export function* deleteSubCategorySaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.DELETE_SUBCATEGORY, deleteSubCategoryEffect);
}
export function* fetchProductTaxonomySaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.FETCH_PRODUCT_TAXONOMY, fetchProductTaxonomyEffect);
}
export function* addXorGroupSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.ADD_XOR_GROUP, saveXorGroupEffect);
}
export function* updateXorGroupSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.UPDATE_XOR_GROUP, saveXorGroupEffect);
}
export function* deleteXorGroupSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.DELETE_XOR_GROUP, deleteXorGroupEffect);
}
export function* addProductGroupSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.ADD_PRODUCT_GROUP, saveProductGroupEffect);
}
export function* updateProductGroupSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.UPDATE_PRODUCT_GROUP, saveProductGroupEffect);
}
export function* deleteProductGroupSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.DELETE_PRODUCT_GROUP, deleteProductGroupEffect);
}
export function* fetchAllProductsSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.FETCH_ALL_INSPIRATIONS_PRODUCTS, fetchAllProductsEffect);
}
export function* bulkEditProductsSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.BULK_EDIT_PRODUCTS, bulkEditProductsEffect);
}

export function* uploadProductImageForBulkEditSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.UPLOAD_PRODUCT_IMAGE_FOR_BULK_EDIT, uploadProductImageForBulkEditEffect);
}

export function* fetchAllProductCategoriesSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.FETCH_PRODUCT_CATEGORIES, fetchAllProductCategoriesEffect);
}
export function* fetchAllProductSubCategoriesSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.FETCH_PRODUCT_SUB_CATEGORIES, fetchAllProductSubCategoriesEffect);
}
export function* deleteProductSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.DELETE_PRODUCT, deleteProductEffect);
}
export function* fetchProductDetailsSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.FETCH_PRODUCT_DETAILS, fetchProductDetailsEffect);
}
export function* fetchProductItemHistorySaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.FETCH_PRODUCT_ITEM_HISTORY, fetchProductItemHistoryEffect);
}

export function* fetchAllProductsWarrantySaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.FETCH_ALL_PRODUCTS_WARRANTY, fetchAllProductWarrantyEffect);
}

export function* fetchAllProductItemsSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.FETCH_PRODUCT_ITEMS_DROPDOWN, fetchAllProductItemsDropdownEffect);
}

export function* fetchAllProductItemsSagaV2(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.FETCH_PRODUCT_ITEMS_DROPDOWN_V2, fetchAllProductItemsDropdownV2Effect);
}

export function* addWarrantySaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.ADD_WARRANTY, addWarrantyEffect);
}

export function* updateWarrantySaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.UPDATE_WARRANTY, updateWarrantyEffect);
}
export function* addProductItemSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.ADD_PRODUCT_ITEM, addProductItemEffect);
}

export function* fetchProductAreaItemSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.FETCH_PRODUCT_AREA_LIST_DROPDOWN, fetchProductAreaListEffect);
}

export function* deleteWarrantySaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.DELETE_WARRANTY, deleteWarrantyEffect);
}

export function* bulkEditWarrantySaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.BULK_EDIT_WARRANTY, bulkEditWarrantyEffect);
}

export function* updateProductItemSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.UPDATE_PRODUCT_ITEM, updateProductItemEffect);
}

export function* updateCoverImageSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.UPDATE_COVER_IMAGE, updateCoverImageEffect);
}

export function* updateProductImagesSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.UPDATE_PRODUCT_IMAGES, updateProductImagesEffect);
}

export function* deleteProductImageSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.DELETE_PRODUCT_IMAGE, deleteProductImageEffect);
}

export function* fetchAreaListDropdownSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.FETCH_QUOTATION_AREA_LIST_DROPDOWN, fetchAreaListDropdownEffect);
}

export function* fetchWarrantyDetailsByCodesEffect(action: AnyAction): ProductsSagaEffect {
	const { warrantyCodes, errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchWarrantyDetailsByCodes, warrantyCodes);
		const warrantyData = data || [];
		yield put({
			type: ProductsActionType.STORE_WARRANTY_DETAILS_BY_CODES,
			payload: Array.isArray(warrantyData) ? warrantyData : []
		});
	} catch (error: unknown) {
		yield put({
			type: ProductsActionType.ERROR_WARRANTY_DETAILS_BY_CODES,
			payload: {
				error
			}
		});
		if (errorCB) {
			errorCB(error?.message || 'Failed to fetch warranty details');
		}
	}
}

export function* fetchWarrantyDetailsByCodesSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.FETCH_WARRANTY_DETAILS_BY_CODES, fetchWarrantyDetailsByCodesEffect);
}

export function* fetchInternalCategoriesEffect(action: AnyAction): ProductsSagaEffect {
	const { errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchInternalCategories);
		yield put({
			type: ProductsActionType.STORE_INTERNAL_CATEGORIES,
			payload: data?.internalCategories || []
		});
	} catch (error: unknown) {
		yield put({
			type: ProductsActionType.ERROR_INTERNAL_CATEGORIES,
			payload: {
				error
			}
		});
		if (errorCB) {
			errorCB(error?.message || 'Failed to fetch internal categories');
		}
	}
}

export function* fetchInternalCategoriesSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.FETCH_INTERNAL_CATEGORIES, fetchInternalCategoriesEffect);
}

export function* uploadProduct3DModelEffect(action: AnyAction): ProductsSagaEffect {
	const { itemId, file, successCB, errorCB } = action.payload;
	try {
		// 1. Request presigned upload URL
		const { data }: any = yield call(uploadDocument, {
			reference_id: itemId,
			reference_type: 'PRODUCT',
			documents: [
				{
					category: 'PRODUCT_3D_MODELS',
					sub_category: 'PRODUCT_3D_MODEL',
					attachments: [{ file: file.name, cover_image: false }]
				}
			]
		});

		// Response shape: { documents_upload_responses: [{ document_id, attachments: [{ document_url, file_name }] }] }
		const responses = data?.documents_upload_responses ?? data?.documentsUploadResponses;
		if (!responses?.length) {
			throw new Error('Upload did not return document');
		}

		const docResponse = responses[0];
		const documentId = docResponse?.document_id ?? docResponse?.documentId;
		const firstAttachment = docResponse?.attachments?.[0];
		const presignedUrl = firstAttachment?.document_url ?? firstAttachment?.documentUrl;

		if (!presignedUrl) {
			throw new Error('Upload response missing document URL');
		}

		// 2. Upload file directly to S3 via presigned URL
		const fileName = firstAttachment?.file_name ?? firstAttachment?.fileName ?? file.name;
		yield call(uploadFile, presignedUrl, file, fileName);

		// 3. Activate the document
		if (documentId != null) {
			yield call(activateDocument, { documentIds: [documentId] });
		}

		// 4. Extract the relative S3 key from the presigned URL.
		// Decode URI components so the stored key matches the actual S3 object key
		// (S3 stores keys with literal spaces/parens, not %20/%28).
		const urlPath = new URL(presignedUrl.split('?')[0]).pathname;
		const rawPath = urlPath.startsWith('/') ? urlPath.slice(1) : urlPath;
		const assetPath = decodeURIComponent(rawPath);

		// 5. Update the product with the model asset path
		yield call(updateProductItem, itemId, { model_3d_asset_path: assetPath });

		successCB(assetPath);
	} catch (error: any) {
		errorCB(error?.message || 'Failed to upload 3D model');
	}
}

export function* removeProduct3DModelEffect(action: AnyAction): ProductsSagaEffect {
	const { itemId, currentAssetPath, successCB, errorCB } = action.payload;
	try {
		// 1. Clear the field on the product
		yield call(updateProductItem, itemId, { model_3d_asset_path: '' });

		// 2. Delete the S3 object if we have the path
		if (currentAssetPath) {
			try {
				yield call(deleteModelAsset, currentAssetPath);
			} catch (deleteErr: unknown) {
				// Non-fatal — field is already cleared, just log
				console.warn('Failed to delete S3 object:', deleteErr);
			}
		}

		successCB();
	} catch (error: any) {
		errorCB(error?.message || 'Failed to remove 3D model');
	}
}

export function* uploadProduct3DModelSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.UPLOAD_PRODUCT_3D_MODEL, uploadProduct3DModelEffect);
}

export function* removeProduct3DModelSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.REMOVE_PRODUCT_3D_MODEL, removeProduct3DModelEffect);
}

export function* fetchDefaultModelsEffect(action: AnyAction): ProductsSagaEffect {
	const { errorCB } = action.payload || {};
	try {
		const { data }: any = yield call(fetchDefaultModels);
		yield put({
			type: ProductsActionType.STORE_DEFAULT_MODELS,
			payload: coerceDefaultModelsRows(data)
		});
	} catch (error: any) {
		yield put({ type: ProductsActionType.STORE_DEFAULT_MODELS, payload: [] });
		errorCB?.(error?.message || 'Failed to fetch default models');
	}
}

export function* uploadSubcategoryDefaultModelEffect(action: AnyAction): ProductsSagaEffect {
	const { subCategoryId, file, successCB, errorCB } = action.payload;
	try {
		const { data }: any = yield call(uploadDocument, {
			reference_id: String(subCategoryId),
			reference_type: 'PRODUCT',
			documents: [
				{
					category: 'PRODUCT_3D_MODELS',
					sub_category: 'PRODUCT_3D_MODEL',
					attachments: [{ file: file.name, cover_image: false }]
				}
			]
		});

		const responses = data?.documents_upload_responses ?? data?.documentsUploadResponses;
		if (!responses?.length) {
			throw new Error('Upload did not return document');
		}

		const docResponse = responses[0];
		const documentId = docResponse?.document_id ?? docResponse?.documentId;
		const firstAttachment = docResponse?.attachments?.[0];
		const presignedUrl = firstAttachment?.document_url ?? firstAttachment?.documentUrl;

		if (!presignedUrl) {
			throw new Error('Upload response missing document URL');
		}

		const fileName = firstAttachment?.file_name ?? firstAttachment?.fileName ?? file.name;
		yield call(uploadFile, presignedUrl, file, fileName);

		if (documentId != null) {
			yield call(activateDocument, { documentIds: [documentId] });
		}

		const urlPath = new URL(presignedUrl.split('?')[0]).pathname;
		const rawPath = urlPath.startsWith('/') ? urlPath.slice(1) : urlPath;
		const assetPath = decodeURIComponent(rawPath);

		yield call(updateSubCategoryDefaultModel, subCategoryId, { default_model_3d_asset_path: assetPath });

		successCB(assetPath);
	} catch (error: any) {
		errorCB(error?.message || 'Failed to upload default 3D model');
	}
}

export function* removeSubcategoryDefaultModelEffect(action: AnyAction): ProductsSagaEffect {
	const { subCategoryId, currentAssetPath, successCB, errorCB } = action.payload;
	try {
		yield call(updateSubCategoryDefaultModel, subCategoryId, { default_model_3d_asset_path: '' });

		if (currentAssetPath) {
			try {
				yield call(deleteModelAsset, currentAssetPath);
			} catch (deleteErr: unknown) {
				console.warn('Failed to delete S3 object:', deleteErr);
			}
		}

		successCB();
	} catch (error: any) {
		errorCB(error?.message || 'Failed to remove default 3D model');
	}
}

export function* fetchDefaultModelsSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.FETCH_DEFAULT_MODELS, fetchDefaultModelsEffect);
}

export function* uploadSubcategoryDefaultModelSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.UPLOAD_SUBCATEGORY_DEFAULT_MODEL, uploadSubcategoryDefaultModelEffect);
}

export function* removeSubcategoryDefaultModelSaga(): ProductsSagaFormEffect {
	yield takeLatest(ProductsActionType.REMOVE_SUBCATEGORY_DEFAULT_MODEL, removeSubcategoryDefaultModelEffect);
}
